import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Heart, Plus, Settings, BarChart3 } from 'lucide-react';
import CosmicCard from '@/components/ui/CosmicCard';
import CosmicButton from '@/components/ui/CosmicButton';

const Tenants: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Tenant Management Hub</h1>
        <p className="text-gray-400 mt-1">Central hub for managing all tenant operations</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Tenant Management */}
        <CosmicCard className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Tenant Management</h3>
              <p className="text-sm text-gray-400">Provision & manage tenants</p>
            </div>
          </div>
          <p className="text-gray-300 mb-4">
            Provision new tenant instances, manage existing deployments, and handle tenant lifecycle operations.
          </p>
          <Link to="/admin/tenant-management">
            <CosmicButton className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
              <Plus className="w-4 h-4 mr-2" />
              Open Tenant Management
            </CosmicButton>
          </Link>
        </CosmicCard>

        {/* Health Dashboard */}
        <CosmicCard className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Health Dashboard</h3>
              <p className="text-sm text-gray-400">Monitor tenant health</p>
            </div>
          </div>
          <p className="text-gray-300 mb-4">
            Real-time monitoring of tenant health, performance metrics, and resource usage across all instances.
          </p>
          <Link to="/admin/tenant-health">
            <CosmicButton className="w-full bg-gradient-to-r from-green-600 to-blue-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              View Health Dashboard
            </CosmicButton>
          </Link>
        </CosmicCard>

        {/* Legacy Tenant Interface */}
        <CosmicCard className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Legacy Management</h3>
              <p className="text-sm text-gray-400">Traditional tenant tools</p>
            </div>
          </div>
          <p className="text-gray-300 mb-4">
            Access to legacy tenant management tools and configurations for backward compatibility.
          </p>
          <CosmicButton variant="secondary" className="w-full">
            <Settings className="w-4 h-4 mr-2" />
            Legacy Interface
          </CosmicButton>
        </CosmicCard>
      </div>

      {/* Quick Stats */}
      <CosmicCard className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-500">12</p>
            <p className="text-sm text-gray-400">Active Tenants</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-500">3</p>
            <p className="text-sm text-gray-400">Provisioning</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-500">99.8%</p>
            <p className="text-sm text-gray-400">Avg Uptime</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-500">156ms</p>
            <p className="text-sm text-gray-400">Avg Response</p>
          </div>
        </div>
      </CosmicCard>

      {/* Recent Activity */}
      <CosmicCard className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-white">Acme Music tenant provisioned successfully</span>
            </div>
            <span className="text-sm text-gray-400">2 hours ago</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-white">Digital Arts Co configuration updated</span>
            </div>
            <span className="text-sm text-gray-400">4 hours ago</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-white">Global Media experiencing high CPU usage</span>
            </div>
            <span className="text-sm text-gray-400">6 hours ago</span>
          </div>
        </div>
      </CosmicCard>
    </div>
  )
}

export default Tenants 